import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from './firebase';
import Alert from './Alert';
import logo from './assets/logo.png';

const SignIn = ({ authError }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setShowPopup(true);
    }
  }, [authError]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistenceType);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      let friendlyError = 'An unknown error occurred. Please try again.';
      if (
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password'
      ) {
        friendlyError = 'Incorrect email or password.';
      } else if (err.code === 'auth/too-many-requests') {
        friendlyError = 'Access temporarily disabled due to too many failed login attempts.';
      }
      setError(friendlyError);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* One soft light source top-left instead of a full-page gradient, so the
          card still reads as the brightest thing on screen. */}
      <div
        className="pointer-events-none absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.35), transparent 65%)' }}
      />

      <div className="relative w-full max-w-[400px]">
        <div className="ui-card p-7 sm:p-8 shadow-pop">
          <div className="flex flex-col items-center mb-7">
            <div className="w-16 h-16 rounded-2xl overflow-hidden border border-ink-500 bg-ink-800 p-1.5">
              <img src={logo} alt="GolpoStream" className="w-full h-full object-cover rounded-xl" />
            </div>
            <h1 className="mt-4 text-xl font-extrabold tracking-tight text-slate-100">
              Golpo<span className="text-accent">Stream</span>
            </h1>
            <p className="text-xs text-slate-500 mt-1">Admin Console</p>
          </div>

          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="ui-label" htmlFor="email">Email</label>
              <input
                type="text"
                id="email"
                className="ui-input"
                placeholder="you@example.com"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="ui-label" htmlFor="psw">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="psw"
                  className="ui-input pr-11"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {/* Typing a long password blind is the usual reason a correct one
                    gets reported as wrong. */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs`} />
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none pt-0.5">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded cursor-pointer accent-[#2dd4bf]"
              />
              <span className="text-xs text-slate-400">Keep me signed in</span>
            </label>

            <button type="submit" disabled={loading} className="ui-btn-primary w-full mt-1">
              {loading
                ? <><span className="ui-spinner w-4 h-4" /> Signing in…</>
                : <><i className="fa-solid fa-right-to-bracket text-xs" /> Sign In</>
              }
            </button>
          </form>
        </div>

        <p className="text-center text-2xs text-slate-600 mt-5">
          Authorised personnel only
        </p>
      </div>

      {showPopup && <Alert message={error} onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default SignIn;
